/**
 * React Hook: useSystemCheck
 * Phase 5: Manages Pre-Flight System Readiness State, Streams & Cleanup
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  SystemCheckId,
  SystemCheckItem,
  SystemCheckOptions,
  ReadinessResult,
} from '../types';
import { SystemCheckService } from '../services/systemCheck.service';

const DEFAULT_CHECKS: SystemCheckItem[] = [
  {
    id: 'browser',
    label: 'Browser Compatibility',
    description: 'Modern browser standard APIs support',
    status: 'idle',
    required: true,
    category: 'environment',
  },
  {
    id: 'secureContext',
    label: 'Secure Origin (HTTPS)',
    description: 'Device media access permission security',
    status: 'idle',
    required: true,
    category: 'environment',
  },
  {
    id: 'mediaDevices',
    label: 'Media Hardware Engine',
    description: 'Hardware audio & video capture interface',
    status: 'idle',
    required: true,
    category: 'environment',
  },
  {
    id: 'microphone',
    label: 'Microphone Permission & Hardware',
    description: 'Voice input capture for AI speech engine',
    status: 'idle',
    required: true,
    category: 'audio',
  },
  {
    id: 'microphoneSignal',
    label: 'Microphone Audio Signal Level',
    description: 'Real-time audio signal volume detection',
    status: 'idle',
    required: true,
    category: 'audio',
  },
  {
    id: 'camera',
    label: 'Camera Feed & Permission',
    description: 'Visual presence & camera signal',
    status: 'idle',
    required: true,
    category: 'video',
  },
  {
    id: 'cameraSignal',
    label: 'Camera Video Signal',
    description: 'Live video track readiness verification',
    status: 'idle',
    required: true,
    category: 'video',
  },
  {
    id: 'speaker',
    label: 'Speaker Output Playback',
    description: 'Audio output for persona voice responses',
    status: 'idle',
    required: false,
    category: 'audio',
  },
  {
    id: 'network',
    label: 'Network Connectivity & Latency',
    description: 'Fast round-trip connection to simulation backend',
    status: 'idle',
    required: true,
    category: 'network',
  },
];

export function useSystemCheck(options: SystemCheckOptions = {}) {
  const {
    requiresMicrophone = true,
    requiresCamera = true,
    requiresSpeaker = true,
    apiPingUrl,
    autoStart = true,
    onReadyChange,
  } = options;

  const requiresMicrophoneRef = useRef(requiresMicrophone);
  requiresMicrophoneRef.current = requiresMicrophone;
  const requiresCameraRef = useRef(requiresCamera);
  requiresCameraRef.current = requiresCamera;
  const requiresSpeakerRef = useRef(requiresSpeaker);
  requiresSpeakerRef.current = requiresSpeaker;

  const [checks, setChecks] = useState<SystemCheckItem[]>(() => {
    return DEFAULT_CHECKS.map((item) => {
      let required = item.required;
      let status = item.status;
      let message = item.message;
      if (item.id === 'microphone' || item.id === 'microphoneSignal') {
        required = requiresMicrophone;
        if (!requiresMicrophone) {
          status = 'passed';
          message = 'Not required for this scenario.';
        }
      }
      if (item.id === 'camera' || item.id === 'cameraSignal') {
        required = requiresCamera;
        if (!requiresCamera) {
          status = 'passed';
          message = 'Not required for this scenario.';
        }
      }
      if (item.id === 'speaker') {
        required = requiresSpeaker;
        if (!requiresSpeaker) {
          status = 'passed';
          message = 'Not required for this scenario.';
        }
      }
      if (item.id === 'mediaDevices') {
        if (!requiresMicrophone && !requiresCamera) {
          required = false;
          status = 'passed';
          message = 'Hardware capture optional.';
        }
      }
      return { ...item, required, status, message };
    });
  });

  const [microphoneStream, setMicrophoneStream] = useState<MediaStream | null>(null);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [speakerConfirmed, setSpeakerConfirmed] = useState<boolean | null>(null);
  const [isPlayingTone, setIsPlayingTone] = useState<boolean>(false);
  const [isCheckingAll, setIsCheckingAll] = useState<boolean>(false);

  // References for cleanup
  const micStreamRef = useRef<MediaStream | null>(null);
  const camStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animFrameRef = useRef<number | null>(null);

  // Synchronize requirement options when props change dynamically
  useEffect(() => {
    setChecks((prev) =>
      prev.map((item) => {
        let required = item.required;
        if (item.id === 'microphone' || item.id === 'microphoneSignal') {
          required = requiresMicrophone;
          if (!requiresMicrophone) {
            return { ...item, required: false, status: 'passed', message: 'Not required for this scenario.' };
          }
        }
        if (item.id === 'camera' || item.id === 'cameraSignal') {
          required = requiresCamera;
          if (!requiresCamera) {
            return { ...item, required: false, status: 'passed', message: 'Not required for this scenario.' };
          }
        }
        if (item.id === 'speaker') {
          required = requiresSpeaker;
          if (!requiresSpeaker) {
            return { ...item, required: false, status: 'passed', message: 'Not required for this scenario.' };
          }
        }
        if (item.id === 'mediaDevices') {
          if (!requiresMicrophone && !requiresCamera) {
            return { ...item, required: false, status: 'passed', message: 'Hardware capture optional.' };
          }
        }
        return { ...item, required };
      })
    );
  }, [requiresMicrophone, requiresCamera, requiresSpeaker]);

  // Helper to update check status
  const updateCheck = useCallback(
    (id: SystemCheckId, updates: Partial<SystemCheckItem>) => {
      setChecks((prev) =>
        prev.map((c) => {
          if (c.id !== id) return c;
          let isReq = updates.required !== undefined ? updates.required : c.required;
          let newStatus = updates.status !== undefined ? updates.status : c.status;
          let newMsg = updates.message !== undefined ? updates.message : c.message;

          if (id === 'camera' || id === 'cameraSignal') {
            if (!requiresCameraRef.current) {
              isReq = false;
              if (newStatus === 'failed') {
                newStatus = 'passed';
                newMsg = 'Not required for this scenario.';
              }
            }
          } else if (id === 'microphone' || id === 'microphoneSignal') {
            if (!requiresMicrophoneRef.current) {
              isReq = false;
              if (newStatus === 'failed') {
                newStatus = 'passed';
                newMsg = 'Not required for this scenario.';
              }
            }
          } else if (id === 'speaker') {
            if (!requiresSpeakerRef.current) {
              isReq = false;
              if (newStatus === 'failed') {
                newStatus = 'passed';
                newMsg = 'Not required for this scenario.';
              }
            }
          } else if (id === 'mediaDevices') {
            if (!requiresMicrophoneRef.current && !requiresCameraRef.current) {
              isReq = false;
              if (newStatus === 'failed') {
                newStatus = 'passed';
                newMsg = 'Hardware capture optional.';
              }
            }
          }
          return { ...c, ...updates, required: isReq, status: newStatus, message: newMsg };
        })
      );
    },
    []
  );

  // Stop media streams safely
  const stopAllStreams = useCallback(() => {
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch {
        // ignore
      }
      sourceRef.current = null;
    }
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch {
        // ignore
      }
      analyserRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        if (audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close();
        }
      } catch {
        // ignore
      }
      audioContextRef.current = null;
    }

    if (micStreamRef.current) {
      SystemCheckService.stopMediaStream(micStreamRef.current);
      micStreamRef.current = null;
      setMicrophoneStream(null);
    }
    if (camStreamRef.current) {
      SystemCheckService.stopMediaStream(camStreamRef.current);
      camStreamRef.current = null;
      setCameraStream(null);
    }
    setAudioLevel(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllStreams();
    };
  }, [stopAllStreams]);

  // Audio level monitoring using AnalyserNode
  const startAudioMonitoring = useCallback((stream: MediaStream) => {
    try {
      const audioCtx = SystemCheckService.createAudioContext();
      if (!audioCtx) return;

      audioContextRef.current = audioCtx;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      analyserRef.current = analyser;

      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);
      sourceRef.current = source;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateMeter = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);

        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const average = sum / bufferLength;
        const normalized = Math.min(100, Math.round((average / 128) * 100));

        setAudioLevel(normalized);
        animFrameRef.current = requestAnimationFrame(updateMeter);
      };

      updateMeter();
    } catch {
      // Audio level metering fallback
    }
  }, []);

  // ─── Individual Check Handlers ──────────────────────────────────────────────

  const checkBrowser = useCallback(async () => {
    updateCheck('browser', { status: 'checking' });
    const result = SystemCheckService.checkBrowserSupport();
    updateCheck('browser', {
      status: result.supported ? 'passed' : 'failed',
      message: result.supported ? 'Supported' : 'Incompatible',
      details: result.details,
    });
    return result.supported;
  }, [updateCheck]);

  const checkSecureContext = useCallback(async () => {
    updateCheck('secureContext', { status: 'checking' });
    const isSecure = SystemCheckService.isSecureContext();
    updateCheck('secureContext', {
      status: isSecure ? 'passed' : 'failed',
      message: isSecure ? 'Secure Origin (HTTPS/localhost)' : 'Insecure Connection',
      details: isSecure
        ? 'Microphone and camera permissions allowed.'
        : 'Device access requires HTTPS or localhost.',
    });
    return isSecure;
  }, [updateCheck]);

  const checkMediaDevices = useCallback(async () => {
    if (!requiresMicrophoneRef.current && !requiresCameraRef.current) {
      updateCheck('mediaDevices', {
        status: 'passed',
        required: false,
        message: 'Hardware Interface Optional',
        details: 'Hardware media capture is not compulsory for this simulation.',
      });
      return true;
    }
    updateCheck('mediaDevices', { status: 'checking' });
    const result = SystemCheckService.checkMediaDevicesSupport();
    updateCheck('mediaDevices', {
      status: result.supported ? 'passed' : 'failed',
      message: result.supported ? 'Hardware Interface Ready' : 'API Unavailable',
      details: result.details,
    });
    return result.supported;
  }, [updateCheck]);

  const checkMicrophone = useCallback(async () => {
    if (!requiresMicrophoneRef.current) {
      updateCheck('microphone', { status: 'passed', required: false, message: 'Not required for this scenario.' });
      updateCheck('microphoneSignal', { status: 'passed', required: false, message: 'Skipped.' });
      return true;
    }

    updateCheck('microphone', { status: 'checking', required: true });
    updateCheck('microphoneSignal', { status: 'checking', required: true });

    try {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        throw new Error('navigator.mediaDevices.getUserMedia is unavailable.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      setMicrophoneStream(stream);

      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length > 0 && audioTracks[0].readyState === 'live') {
        updateCheck('microphone', {
          status: 'passed',
          required: true,
          message: 'Connected & Permitted',
          details: audioTracks[0].label || 'Default microphone stream active',
        });

        startAudioMonitoring(stream);

        updateCheck('microphoneSignal', {
          status: 'passed',
          required: true,
          message: 'Signal Detected',
          details: 'Microphone is receiving audio input.',
        });
        return true;
      } else {
        updateCheck('microphone', {
          status: 'failed',
          required: true,
          message: 'No live audio track',
          details: 'Microphone stream is not active.',
        });
        updateCheck('microphoneSignal', { status: 'failed', required: true, message: 'No signal' });
        return false;
      }
    } catch (err) {
      if (!requiresMicrophoneRef.current) {
        updateCheck('microphone', {
          status: 'passed',
          required: false,
          message: 'Not required for this scenario.',
          details: 'Microphone is optional.',
        });
        updateCheck('microphoneSignal', {
          status: 'passed',
          required: false,
          message: 'Skipped',
          details: 'Microphone signal check skipped.',
        });
        return true;
      }
      const errInfo = SystemCheckService.mapMediaError(err, 'microphone');
      updateCheck('microphone', {
        status: 'failed',
        required: true,
        message: errInfo.message,
        details: errInfo.details,
      });
      updateCheck('microphoneSignal', {
        status: 'failed',
        required: true,
        message: 'Microphone unverified',
        details: 'Check microphone permissions.',
      });
      return false;
    }
  }, [updateCheck, startAudioMonitoring]);

  const checkCamera = useCallback(async () => {
    if (!requiresCameraRef.current) {
      updateCheck('camera', {
        status: 'passed',
        required: false,
        message: 'Not required for this scenario.',
        details: 'Camera is optional for this simulation.',
      });
      updateCheck('cameraSignal', {
        status: 'passed',
        required: false,
        message: 'Optional (Audio-only simulation)',
        details: 'Video signal check skipped.',
      });
      return true;
    }

    updateCheck('camera', { status: 'checking', required: true });
    updateCheck('cameraSignal', { status: 'checking', required: true });

    try {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        throw new Error('navigator.mediaDevices.getUserMedia is unavailable.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      camStreamRef.current = stream;
      setCameraStream(stream);

      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length > 0 && videoTracks[0].readyState === 'live') {
        const track = videoTracks[0];
        updateCheck('camera', {
          status: 'passed',
          required: true,
          message: 'Connected & Permitted',
          details: track.label || 'Default camera stream active',
        });
        updateCheck('cameraSignal', {
          status: 'passed',
          required: true,
          message: 'Live Feed Active',
          details: 'Camera video signal verified.',
        });
        return true;
      } else {
        updateCheck('camera', {
          status: 'failed',
          required: true,
          message: 'No live video track',
          details: 'Camera stream is not active.',
        });
        updateCheck('cameraSignal', { status: 'failed', required: true, message: 'No video signal' });
        return false;
      }
    } catch (err) {
      if (!requiresCameraRef.current) {
        updateCheck('camera', {
          status: 'passed',
          required: false,
          message: 'Not required for this scenario.',
          details: 'Camera is optional.',
        });
        updateCheck('cameraSignal', {
          status: 'passed',
          required: false,
          message: 'Skipped',
          details: 'Camera signal check skipped.',
        });
        return true;
      }
      const errInfo = SystemCheckService.mapMediaError(err, 'camera');
      updateCheck('camera', {
        status: 'failed',
        required: true,
        message: errInfo.message,
        details: errInfo.details,
      });
      updateCheck('cameraSignal', {
        status: 'failed',
        required: true,
        message: 'Camera unverified',
        details: 'Check camera permissions.',
      });
      return false;
    }
  }, [updateCheck]);

  const checkNetwork = useCallback(async () => {
    updateCheck('network', { status: 'checking' });
    const result = await SystemCheckService.checkNetworkHealth(apiPingUrl);
    updateCheck('network', {
      status: result.online ? 'passed' : 'failed',
      message: result.online ? (result.latencyMs ? `${result.latencyMs}ms Ping` : 'Online') : 'Offline',
      details: result.message,
    });
    return result.online;
  }, [apiPingUrl, updateCheck]);

  // Speaker Chime Test Trigger
  const playTestChime = useCallback(async () => {
    setIsPlayingTone(true);
    try {
      await SystemCheckService.playSpeakerTestTone();
    } finally {
      setTimeout(() => setIsPlayingTone(false), 500);
    }
  }, []);

  const confirmSpeaker = useCallback(
    (heard: boolean) => {
      setSpeakerConfirmed(heard);
      updateCheck('speaker', {
        status: heard ? 'passed' : 'warning',
        message: heard ? 'Audio Output Confirmed' : 'Audio Not Heard',
        details: heard
          ? 'Learner confirmed speaker output is functional.'
          : 'Check volume and audio output device.',
      });
    },
    [updateCheck]
  );

  // Run all checks in sequence
  const runAllChecks = useCallback(async () => {
    setIsCheckingAll(true);
    stopAllStreams();

    await checkBrowser();
    await checkSecureContext();
    await checkMediaDevices();
    await checkMicrophone();
    await checkCamera();
    await checkNetwork();

    // Speaker check default idle state
    if (requiresSpeaker && speakerConfirmed === null) {
      updateCheck('speaker', {
        status: 'idle',
        message: 'Awaiting Sound Test',
        details: 'Click "Play Test Sound" to verify speaker output.',
      });
    }

    setIsCheckingAll(false);
  }, [
    stopAllStreams,
    checkBrowser,
    checkSecureContext,
    checkMediaDevices,
    checkMicrophone,
    checkCamera,
    checkNetwork,
    requiresSpeaker,
    speakerConfirmed,
    updateCheck,
  ]);

  // Retry an individual check item
  const retryCheck = useCallback(
    async (id: SystemCheckId) => {
      switch (id) {
        case 'browser':
          return checkBrowser();
        case 'secureContext':
          return checkSecureContext();
        case 'mediaDevices':
          return checkMediaDevices();
        case 'microphone':
        case 'microphoneSignal':
          return checkMicrophone();
        case 'camera':
        case 'cameraSignal':
          return checkCamera();
        case 'network':
          return checkNetwork();
        case 'speaker':
          return playTestChime();
        default:
          return false;
      }
    },
    [
      checkBrowser,
      checkSecureContext,
      checkMediaDevices,
      checkMicrophone,
      checkCamera,
      checkNetwork,
      playTestChime,
    ]
  );

  // Auto-run on mount if autoStart is true
  useEffect(() => {
    if (autoStart) {
      runAllChecks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  // Compute overall readiness result
  const readiness: ReadinessResult = SystemCheckService.calculateReadiness(
    checks,
    speakerConfirmed,
    audioLevel
  );

  useEffect(() => {
    onReadyChange?.(readiness.isReady);
  }, [readiness.isReady, onReadyChange]);

  return {
    checks,
    readiness,
    isReady: readiness.isReady,
    isCheckingAll,
    microphoneStream,
    cameraStream,
    audioLevel,
    speakerConfirmed,
    isPlayingTone,
    runAllChecks,
    retryCheck,
    playTestChime,
    confirmSpeaker,
    stopAllStreams,
  };
}
